// Countries list — add a country here; each row opens the detail (Locale / Ledger codes / Pricing).
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { focusModal } from '../shared/modal.js';
import { DATE_FORMATS } from '../shared/formats.js';
import { rowDelete } from '../shared/confirm.js';
import { flashId, revealRow, saveList, takeList } from '../shared/listnav.js';

export function countriesScreen(){
  const rows    = signal([]);
  const total   = signal(0);
  const page    = signal(1);
  const pages   = signal(1);
  const perPage = signal(DEFAULT_PER_PAGE);
  const loading = signal(true);
  const langs   = signal([]);
  const currs   = signal([]);
  let timer, lastQ = '';

  // add-country form state
  const formOpen = signal(false);
  const fIso2 = signal(''), fIso3 = signal(''), fName = signal(''), fLang = signal(''), fCurr = signal('');
  const fDate = signal('yyyy-MM-dd');
  const formErr = signal(''), saving = signal(false), invalid = signal({});

  // inline delete state
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');

  async function load(locateId){
    loading.value = true;
    const params = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ && lastQ.trim()) params.set('q', lastQ.trim());
    if(locateId) params.set('locate', String(locateId));
    try { const r = await api.get('/admin/countries?' + params.toString());
          rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
          if(locateId && r.locate_page) page.value = r.locate_page; }
    catch { rows.value = []; total.value = 0; pages.value = 1; }
    loading.value = false;
  }
  // returning from a country detail? restore the page/search + scroll position (issue #13)
  const _saved = takeList('countries');
  if(_saved){ page.value = _saved.page || 1; perPage.value = _saved.perPage || DEFAULT_PER_PAGE; lastQ = _saved.q || ''; }
  load().then(() => {
    if(_saved && _saved.scroll) requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, _saved.scroll)));
  });
  (async () => { try { langs.value = (await api.get('/admin/languages?per_page=200')).data || []; } catch {} })();
  (async () => { try { currs.value = (await api.get('/admin/currencies?per_page=200')).data || []; } catch {} })();

  function openCountry(c){
    saveList('countries', { page: page.value, perPage: Number(perPage.value), q: lastQ });
    navigate('/countries/' + c.id);
  }
  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }

  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/admin/countries/' + id); confirmId.value = null; await load(); }
    catch(err){ delErr.value = (err.data && err.data.error) || err.message || 'Delete failed'; }
    deleting.value = false;
  }

  function openCreate(){
    fIso2.value = ''; fIso3.value = ''; fName.value = ''; fLang.value = ''; fCurr.value = '';
    fDate.value = 'yyyy-MM-dd';
    formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal();
  }
  const clearInvalid = k => { if(invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };
  function closeForm(){ formOpen.value = false; }
  async function save(e){
    e.preventDefault();
    const inv = {};
    if(!fIso2.value.trim()) inv.iso2 = true;
    if(!fName.value.trim()) inv.name = true;
    if(!fLang.value)        inv.lang = true;
    if(!fCurr.value)        inv.curr = true;
    invalid.value = inv;
    if(Object.keys(inv).length){ formErr.value = 'Please fill in the highlighted fields.'; return; }
    saving.value = true; formErr.value = '';
    const body = { iso2: fIso2.value.trim(), iso3: fIso3.value.trim() || null, name: fName.value.trim(),
                   default_language_id: Number(fLang.value), default_currency_id: Number(fCurr.value),
                   date_format: fDate.value };
    try { const res = await api.post('/admin/countries', body); formOpen.value = false;
          await revealRow(load, res && res.id); }
    catch(err){ formErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    saving.value = false;
  }

  // Single reactive block: rows + pager together. A standalone ${() => pager(...)} slot appends
  // on every re-render in this tina4js version; the list block replaces cleanly, so the pager
  // rides inside it. See shared/pager.js.
  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No countries match.</div>`;
    const cId = confirmId.value, del = deleting.value, err = delErr.value;   // subscribe to delete state
    const hl = flashId.value;                                                // row to highlight
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${err ? html`<div class="alert alert-danger py-2 mb-2">${err}</div>` : null}
      ${pg()}
      <div class="row-list">${rows.value.map(c => html`
        <div class=${'row-card' + (hl === c.id ? ' row-hl' : '')} @click=${() => openCountry(c)}>
          <span class="iso-badge">${c.iso2}</span>
          <span class="row-main"><b>${c.name}</b><span class="row-sub">${c.iso3 || ''}</span></span>
          ${rowDelete({ armed: cId === c.id, busy: del, error: cId === c.id ? err : '',
                        onArm: () => armDelete(c.id), onCancel: cancelDelete, onDelete: () => doDelete(c.id) })}
          <span class="row-chevron">${icon('chevron-right', {size: 14})}</span>
        </div>`)}</div>
      ${pg()}
    </div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">Countries</h1>
          <div class="page-sub">Each country drives its language, ledger codes &amp; pricing</div>
        </div>
        <button class="btn btn-primary" @click=${openCreate}>+ Add country</button>
      </div>
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search name or ISO…" @input=${onSearch}>
        ${perPageSelect(perPage.value, onPer)}
      </div>
      <div class="count-line">${total} countries</div>
      ${list}

      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head"><h3>Add country</h3><button type="button" class="modal-x" @click=${closeForm}>✕</button></div>
          <label class="form-label">ISO-2 code</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.iso2 ? ' is-invalid' : '')} maxlength="2" .value=${fIso2} @input=${e => { fIso2.value = e.target.value; clearInvalid('iso2'); }} placeholder="e.g. PA">
          <label class="form-label">ISO-3 code</label>
          <input class="form-control mb-2" maxlength="3" .value=${fIso3} @input=${e => { fIso3.value = e.target.value; }} placeholder="e.g. PAN (optional)">
          <label class="form-label">Name</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.name ? ' is-invalid' : '')} .value=${fName} @input=${e => { fName.value = e.target.value; clearInvalid('name'); }} placeholder="e.g. Panama">
          <label class="form-label">Default language</label>
          <select class=${() => 'form-control mb-2' + (invalid.value.lang ? ' is-invalid' : '')} .value=${fLang} @change=${e => { fLang.value = e.target.value; clearInvalid('lang'); }}>
            <option value="">— Select a language —</option>
            ${() => langs.value.map(l => html`<option value=${l.id}>${l.name} (${l.code})</option>`)}
          </select>
          <label class="form-label">Default currency</label>
          <select class=${() => 'form-control mb-2' + (invalid.value.curr ? ' is-invalid' : '')} .value=${fCurr} @change=${e => { fCurr.value = e.target.value; clearInvalid('curr'); }}>
            <option value="">— Select a currency —</option>
            ${() => currs.value.map(c => html`<option value=${c.id}>${c.symbol} ${c.code} — ${c.name}</option>`)}
          </select>
          <label class="form-label">Default date format</label>
          <select class="form-control mb-2" .value=${fDate} @change=${e => { fDate.value = e.target.value; }}>
            ${DATE_FORMATS.map(d => html`<option value=${d.v}>${d.v} — ${d.ex}</option>`)}
          </select>
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr}</div>` : null}
          <div class="form-actions">
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>`;

  return shell('countries', 'Countries', content);
}
